import { del, get, post, put } from './requests.js'

// Every call here uses `transformPayload: false`.
//
// The default camelizes response keys and decamelizes request keys, which
// would quietly rename things that have to survive intact: mapping ids
// (`liveramp_us`), partner setting keys (`ttd_cpm_rate` - the API looks them
// up by name in enums/custom-audiences/api_integration_fields), and the state
// values on a push. Keeping snake_case end to end is simpler than listing
// exceptions.
const raw = { transformPayload: false, removeTrailingSlash: true }

// ---------------------------------------------------------------------------
// partners
// ---------------------------------------------------------------------------

// Every partner, what each supports, and the fields its form needs. The form
// is built from this rather than from conditionals per partner.
export const getPartners = () => get(['api-integrations'], raw)

// Top level folders in a partner's system. Partners with no folder concept
// answer with an empty list, so the caller can hide the step.
export const getPartnerFolders = (partner) => get(['api-integrations', partner, 'folders'], raw)

// Some partners want the folder's own id chosen by us - TTD does not generate
// one. Left out, the API derives it from the name ("Data Alliance" ->
// "DATA_ALLIANCE", matching TTD's existing folders) - or, for Yahoo, makes
// one. `pricing` ({ cpm, media_types }) is the folder's rate, for partners
// whose segments take their folder's (Yahoo).
export const createPartnerFolder = (partner, name, id, pricing) =>
  post(['api-integrations', partner, 'folders'], raw, { name, id, pricing })

// ---------------------------------------------------------------------------
// one segment in each partner
// ---------------------------------------------------------------------------

// How one taxonomy segment looks in each partner its taxonomy distributes to:
// one row per distribution that goes to a partner we have an integration for,
// with its ID there, asked live, beside what our own pushes recorded.
export const getAudienceApiIntegrations = (audienceId) =>
  get(['api-integrations', 'audience', audienceId], raw)

// ---------------------------------------------------------------------------
// coverage - which of a taxonomy's audiences each partner has
// ---------------------------------------------------------------------------

// Every taxonomy's coverage in every partner we scan, as of the last nightly
// scan - counts only - with that scan's record and the partners it reads.
export const getCoverage = () => get(['api-integrations', 'coverage'], raw)

// One taxonomy's coverage, worked out now from the partners' last lists, with
// the audiences behind each count. Counts a push made since the lists were
// taken. `{ audiences: 'all' }` lists the synced and blocklisted ones too -
// every audience, for the coverage page's report.
export const getTaxonomyCoverage = (taxonomyId, params) =>
  get(['api-integrations', 'coverage', 'taxonomy', taxonomyId], raw, params)

// ---------------------------------------------------------------------------
// pushes
// ---------------------------------------------------------------------------

export const getPushes = (params) => get(['taxonomy-api-integrations'], raw, params)

export const getPush = (id) => get(['taxonomy-api-integrations', id], raw)

export const createPush = (payload) => post(['taxonomy-api-integrations'], raw, payload)

export const updatePush = (id, payload) => put(['taxonomy-api-integrations', id], raw, payload)

export const deletePush = (id) => del(['taxonomy-api-integrations', id], raw)

// ---------------------------------------------------------------------------
// running
// ---------------------------------------------------------------------------

export const runPush = (id) => post(['taxonomy-api-integrations', 'run', id], raw, {})

export const cancelPush = (id) => post(['taxonomy-api-integrations', 'cancel', id], raw, {})

// Carries on with a push that stopped partway - only what is still pending.
// Rarely needed for a stall, since the server picks those up by itself within
// a minute; this is the escape hatch for one that has used up its retries.
export const resumePush = (id) => post(['taxonomy-api-integrations', 'resume', id], raw, {})

// "Run again": a fresh draft with the same settings. A push that has run is a
// record of what happened, so it is copied rather than run a second time.
// Answers with the new draft.
export const copyPush = (id) => post(['taxonomy-api-integrations', 'copy', id], raw, {})

// A fresh draft holding just the audiences that failed. Answers with it.
export const retryFailed = (id) => post(['taxonomy-api-integrations', 'retry-failed', id], raw, {})

// ---------------------------------------------------------------------------
// form helpers
// ---------------------------------------------------------------------------

// Live before/after for the name changes, on real audiences from this
// taxonomy: exactly `audienceIds` (at most 50), or a page of the whole
// taxonomy (`skip`, `limit`), answered with its `total`. With no taxonomy,
// only `audienceIds`, from any taxonomy. Given a `mappingId`,
// each row carries the audience's ID in that space. Calls no partner - this is
// form feedback, not a dry run.
export const previewNames = (
  taxonomyId,
  nameChanges,
  { audienceIds, skip, limit, mappingId } = {}
) =>
  post(['taxonomy-api-integrations', 'preview-names'], raw, {
    taxonomy_id: taxonomyId,
    name_changes: nameChanges,
    audience_ids: audienceIds,
    skip,
    limit,
    mapping_id: mappingId,
  })

// Match a list of mapping ids against the audiences in this taxonomy - or,
// with no `taxonomyId`, across every taxonomy. The mapping id has to be chosen
// first - it says which column of audience.ids to compare against. Returns
// counts, the ids that matched nothing and, across taxonomies, the ids more
// than one audience has, which are left out.
export const resolveCsv = (taxonomyId, mappingId, values) =>
  post(['taxonomy-api-integrations', 'resolve-csv'], raw, {
    taxonomy_id: taxonomyId,
    mapping_id: mappingId,
    values,
  })

// ---------------------------------------------------------------------------
// results
// ---------------------------------------------------------------------------

// One row per audience. Paged and filterable by state, because a push can run
// to thousands.
export const getPushCalls = (pushId, params) =>
  get(['taxonomy-api-integration-calls'], raw, { push_id: pushId, ...params })

// The same rows as a CSV, all of them rather than a page - or only those in
// `state`. CSV, so it goes around the API client (which always parses JSON).
// The links in it open the Datasphere it was downloaded from.
export const getPushResultsCsv = (pushId, { state } = {}) => {
  const params = new URLSearchParams({ datasphere_url: window.location.origin })
  if (state) params.append('state', state)

  return fetch(`${window._api.backend}/taxonomy-api-integrations/results/${pushId}.csv?${params}`, {
    method: 'GET',
    headers: {
      Accept: 'text/csv',
      Authorization: `Bearer ${window._api.token}`,
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`)
    }
    return response.text()
  })
}
